import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import AnimateHeight from 'react-animate-height';

import {
  Avatar,
  AvatarGroup,
  Badge,
  Button,
  MenuItem,
  Select,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';

import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { toTitleCase, truncateStr } from '../../utils';
import { darkInitials } from '../../utils/globals';

import { AppDataContext, SubNavStateContext } from '../../contexts';

import determineBadgeColor from './utils';

import useFetch from '../../hooks/useFetch';

import './ProjectStatusCard.scss';

export default function ProjectStatusCard({
  projectData,
  projectList,
  setProjectList,
  dataEntry,
  preparer,
  deliver,
  setSearchQuery=()=>{}
}) {
  const {enterpriseUsers, setProjectToView, setInvestorCompanyDetailToView, setEnterpriseProjects} = useContext(AppDataContext)
  const { setSubNavState } = useContext(SubNavStateContext);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [priority, setPriority] = useState(projectData.priority);
  const [dropdownIsOpen, setDropdownIsOpen] = useState({
    priorityDropdown: false,
    assignedToDropdown: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [insideElementHover, setInsideElementHover] = useState(false);

  const [dataEntryPerProject, setDataEntryPerProject] = useState([]);
  const [preparerPerProject, setPreparerPerProject] = useState([]);
  const [reviewerPerProject, setReviewerPerProject] = useState([]);
  const [lastModifiedUser, setLastModifiedUser] = useState(null);

  const [topLevelAvatar, setTopLevelAvatar] = useState([]);

  const [, updatePriorityRequest] = useFetch();
  const [, deleteProjectRequest] = useFetch();

  const [isDeleting, setIsDeleting] = useState(false);

  const findEnterpriseUser = (userId) => (enterpriseUsers || []).find((user) => user.accountId === userId);
  const projectName = projectData?.projectName || '';
  const investorFirmName = projectData?.investorFirmName || '';
  function getInitials(name) {
    if (name) {
      const splitName = name.split(' ');
      const firstInitial = Array.from(splitName[0])[0];
      const lastInitial = splitName[1] ? Array.from(splitName[1])[0] : '';
      return toTitleCase(firstInitial) + toTitleCase(lastInitial);
    }
    return null;
  }

  function parseAssignees() {
    if (projectData?.assignedUsers?.dataEntry) {
      setDataEntryPerProject(Object.entries(projectData.assignedUsers.dataEntry));
    }
    if (projectData?.assignedUsers?.preparer) {
      setPreparerPerProject(Object.entries(projectData.assignedUsers.preparer));
    }
    if (projectData?.assignedUsers?.reviewer) {
      setReviewerPerProject(Object.entries(projectData.assignedUsers.reviewer));
    }
    if (projectData.priority !== 0) setPriority(projectData.priority);
    if (projectData.lastRequestUserId) setLastModifiedUser(findEnterpriseUser(projectData.lastRequestUserId));
  }

  useEffect(() => {
    setTopLevelAvatar(dataEntry ? dataEntryPerProject : preparer ? preparerPerProject : deliver ? reviewerPerProject : []);
  }, [dataEntryPerProject, preparerPerProject, reviewerPerProject]);

  useEffect(() => { parseAssignees(); }, [enterpriseUsers, projectData, dataEntry, preparer, deliver]);


  async function updatePriority(value) {
    const priorityData = {
      projectId: projectData.projectId,
      priority: value,
      enterpriseCompanyId: projectData.companyId,
    };
    setProjectList((prevState) => prevState.map((project) => {
      if (project.projectId === projectData.projectId) {
        return { ...project, priority: value };
      }
      return project;
    }));
    updatePriorityRequest({
      url: '/homepages/enterprise/update-priority',
      method: 'post',
      body: priorityData,
      bodyIds: ['requestUserId'],
      onSuccess: () => {
        setSubNavState((prevState) => ({
          ...prevState,
          updateProjectPriority: {
            projectId: projectData.projectId,
            priority: value,
          },
        }));
      },
    });
  }

  function updateProjectList(value) {
    const projectListCopy = [...projectList];
    const projectDataCopy = projectListCopy[projectListCopy.findIndex((project) => project.projectId === projectData.projectId)];
    projectDataCopy.priority = value.toString();
    setProjectList(projectListCopy);
    setPriority(value.toString());
    setDropdownIsOpen({ ...dropdownIsOpen, priorityDropdown: false });
  }

  const handleDeleteProject = () => {
    setIsDeleting(true);
    deleteProjectRequest({
      url: `/homepages/project-cards/${projectData.projectId}?enterprise_company_id=${projectData.companyId}&request_user_id=${projectData.requestUserId}`,
      method: 'delete',
      onSuccess: (response) => {
        console.log('Delete response:', response);
        setIsDeleting(false);
        setDeleteSuccess(true);
        
        // Wait for animation to complete before closing
        setTimeout(() => {
          setDeleteDialogOpen(false);
          // Update local project list
          setProjectList((prevList) => prevList.filter((project) => project.projectId !== projectData.projectId));
          // Update enterpriseProjects in context
          setEnterpriseProjects((prevProjects) => prevProjects.filter((project) => project.projectId !== projectData.projectId));
          // Reset states after modal closes
          setTimeout(() => {
            setDeleteSuccess(false);
          }, 300);
        }, 1500);
      },
      onError: (error) => {
        console.error('Error deleting project:', error);
        setDeleteError(error.detail || 'Failed to delete project. Please try again.');
        setDeleteDialogOpen(false);
        setIsDeleting(false);
      }
    });
  };

  const handleCloseSnackbar = () => {
    setDeleteError(null);
    setDeleteSuccess(false);
    setSuccessMessage('');
  };

  return (
    <div
      className={`ProjectStatusCard${!insideElementHover ? ' can-hover' : ''}`}
    >
      <div
        role="button"
        className={`card-btn${insideElementHover ? ' can-hover' : ''}`}
        onClick={() => {
          if (!insideElementHover) {
            setSearchQuery('');
            setProjectToView(projectData);
          }
        }}
        onKeyDown={(e) => (e.key === 'Enter' && setProjectToView(projectData))}
        tabIndex={0}
      >
        <div className="card-header">
          <span className="service-name">
            {'820' || ''}
          </span>
          <div className="card-header-info">
            {projectName.length > 25 ? (
              <Tooltip
                id="name-tooltip"
                title={projectName}
                disableFocusListener
                arrow
                placement="top"
              >
                <span className="fund-name">{`${truncateStr(projectName, 25)}`}</span>
              </Tooltip>
            ) : (
              <span className="fund-name">{projectName || 'Untitled project'}</span>
            )}
            {investorFirmName.length > 25 ? (
              <Tooltip
                id="name-tooltip"
                title={investorFirmName}
                disableFocusListener
                arrow
                placement="top"
              >
                <span>{`${truncateStr(investorFirmName, 24)}`}</span>
              </Tooltip>
            ) : (
              <span>{investorFirmName || 'Unknown investor'}</span>
            )}
          </div>
          <div className="priority-icon-wrapper">
            {priority === '1' ? (
              <NotificationImportantOutlinedIcon className="priority-icon" />
            ) : priority === '3' && (
              <ArrowDownwardIcon />
            )}
          </div>
        </div>
        <div className="status-badge-wrapper">
          {projectData.blockerStatus && (
            <span className={`status-badge ${determineBadgeColor(projectData.blockerStatus)} sub-status`}>
              {projectData.blockerStatus}
            </span>
          )}
          <span className={`status-badge ${determineBadgeColor(projectData.status)}`}>
            {projectData?.insertUserId}
            {projectData.status}
          </span>
        </div>
        <div className="card-footer-wrapper">
          <div className="card-footer-info">
            <span className={`date-info-due ${!topLevelAvatar.length ? 'no-assignees' : ''}`}>
              <ThemeProvider
                theme={createTheme({
                  components: {
                    MuiAvatarGroup: {
                      styleOverrides: {
                        root: ({ ownerState: { max } }) => ({
                          ...[...Array(max)].reduce(
                            (result, curr, index) => ({
                              ...result,
                              [`& > .MuiAvatar-root:nth-of-type(${index + 1})`]: {
                                zIndex: max - index,
                              },
                            }),
                            {},
                          ),
                        }),
                      },
                    },
                  },
                })}
              >
                <AvatarGroup max={5}>
                  {topLevelAvatar.length > 0 ? (
                    topLevelAvatar.map((user) => (
                      <Tooltip
                        key={user[0]}
                        disableInteractive
                        title={(
                          <Typography style={{ whiteSpace: 'pre-line' }}>
                            {
                              `${dataEntry ? 'Data Entry' : preparer ? 'Preparer' : 'Deliver'}
                      ${findEnterpriseUser(user[0])?.firstName} ${findEnterpriseUser(user[0])?.lastName}`
                            }
                          </Typography>
                        )}
                        PopperProps={{ className: 'bottom-arrow-tooltip' }}
                        placement="bottom"
                        arrow
                      >
                        <Avatar
                          style={{
                            backgroundColor: findEnterpriseUser(user[0])?.avatarColor,
                            color: darkInitials.includes(findEnterpriseUser(user[0])?.avatarColor) ? '#49454F' : '#FAFAFD',
                            marginLeft: 'calc(1.75rem * -0.5)',
                          }}
                          onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                          onMouseLeave={() => setInsideElementHover(false)}
                        >
                          {getInitials(`${findEnterpriseUser(user[0])?.firstName} ${findEnterpriseUser(user[0])?.lastName}`)}
                        </Avatar>
                      </Tooltip>
                    ))
                  ) : (
                    <Tooltip
                      disableInteractive
                      title={(
                        <Typography style={{ whiteSpace: 'pre-line' }}>
                          Unassigned
                        </Typography>
                      )}
                      PopperProps={{ className: 'bottom-arrow-tooltip' }}
                      placement="bottom"
                      arrow
                    >
                      <Avatar
                        onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                        onMouseLeave={() => setInsideElementHover(false)}
                      >
                        <PersonOutlineOutlinedIcon />
                      </Avatar>
                    </Tooltip>
                  )}
                </AvatarGroup>
              </ThemeProvider>
              {projectData?.numberOfCompanies ?? 0}
              &nbsp;companies in project
              <br />
              Info due:&nbsp;
              {projectData?.clientInfoDueDate ? moment(projectData?.clientInfoDueDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}
              <br />
              Created:&nbsp;
              {projectData?.createdDate ? moment(projectData?.createdDate).format('MM/DD/YYYY') : ''}
            </span>
          </div>
          <button
            type="button"
            className="card-expand-chevron"
            onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
            onMouseLeave={() => setInsideElementHover(false)}
            onClick={(e) => {
              e.stopPropagation();
              setDropdownHeight(dropdownHeight ? 0 : 'auto');
            }}
          >
            <ExpandMoreIcon className={`circle-chevron${dropdownHeight ? ' upward' : ' downward'}`} />
          </button>
        </div>
        <AnimateHeight
          duration={500}
          height={dropdownHeight}
        >
          <hr className="card-spacer" />
          <div className="expanded-content">
            <div className="expanded-content-wrapper">
              <div className="links-wrapper">
                <div className="assigned-to-section">
                  <h6>Assigned to</h6>
                  <hr className="card-spacer" />
                  <div className="assigned-to-title">
                    <span>Data entry</span>
                    <div
                      className="assignee-avatars"
                      onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                      onMouseLeave={() => setInsideElementHover(false)}
                    >
                      {dataEntryPerProject.map((assignee) => {
                        const enterpriseUser = findEnterpriseUser(assignee[0]);
                        return (
                          <Tooltip
                            key={`${enterpriseUser?.firstName}${(assignee[1] || []).length}${enterpriseUser?.avatarColor}`}
                            disableInteractive
                            title={(
                              <Typography style={{ whiteSpace: 'pre-line' }}>
                                {`${enterpriseUser?.firstName} ${enterpriseUser?.lastName}\n${(assignee[1] || []).length} companies in this project`}
                              </Typography>
                            )}
                            PopperProps={{ className: 'bottom-arrow-tooltip' }}
                            placement="bottom"
                            arrow
                          >
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={(
                                <Avatar
                                  className="working-companies"
                                  sx={{ bgcolor: `${enterpriseUser?.avatarColor}` }}
                                >
                                  {(assignee[1] || []).length}
                                </Avatar>
                              )}
                            >
                              <Avatar
                                style={{
                                  backgroundColor: enterpriseUser?.avatarColor,
                                  color: darkInitials.includes(enterpriseUser?.avatarColor) ? '#49454F' : '#FAFAFD',
                                }}
                              >
                                {getInitials(`${enterpriseUser?.firstName} ${enterpriseUser?.lastName}`)}
                              </Avatar>
                            </Badge>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                  <div className="assigned-to-title">
                    <span>Preparer</span>
                    <div
                      className="assignee-avatars"
                      onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                      onMouseLeave={() => setInsideElementHover(false)}
                    >
                      {preparerPerProject.map((assignee) => {
                        const enterpriseUser = findEnterpriseUser(assignee[0]);
                        return (
                          <Tooltip
                            key={`${enterpriseUser?.firstName}${(assignee[1] || []).length}${enterpriseUser?.avatarColor}`}
                            disableInteractive
                            title={(
                              <Typography style={{ whiteSpace: 'pre-line' }}>
                                {`${enterpriseUser?.firstName} ${enterpriseUser?.lastName}\n${(assignee[1] || []).length} companies in this project`}
                              </Typography>
                            )}
                            PopperProps={{ className: 'bottom-arrow-tooltip' }}
                            placement="bottom"
                            arrow
                          >
                            <Badge
                              key={`${enterpriseUser?.firstName}${(assignee[1] || []).length}${enterpriseUser?.avatarColor}`}
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={(
                                <Avatar className="working-companies">
                                  {(assignee[1] || []).length}
                                </Avatar>
                              )}
                            >
                              <Avatar
                                style={{
                                  backgroundColor: enterpriseUser?.avatarColor,
                                  color: darkInitials.includes(enterpriseUser?.avatarColor) ? '#49454F' : '#FAFAFD',
                                }}
                              >
                                {getInitials(`${enterpriseUser?.firstName} ${enterpriseUser?.lastName}`)}
                              </Avatar>
                            </Badge>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                  <div className="assigned-to-title">
                    <span>Reviewer</span>
                    <div
                      className="assignee-avatars"
                      onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                      onMouseLeave={() => setInsideElementHover(false)}
                    >
                      {reviewerPerProject.map((assignee) => {
                        const enterpriseUser = findEnterpriseUser(assignee[0]);
                        return (
                          <Tooltip
                            key={`${enterpriseUser?.firstName}${(assignee[1] || []).length}${enterpriseUser?.avatarColor}`}
                            disableInteractive
                            title={(
                              <Typography style={{ whiteSpace: 'pre-line' }}>
                                {`${enterpriseUser?.firstName} ${enterpriseUser?.lastName}\n${(assignee[1] || []).length} companies in this project`}
                              </Typography>
                            )}
                            PopperProps={{ className: 'bottom-arrow-tooltip' }}
                            placement="bottom"
                            arrow
                          >
                            <Badge
                              key={`${enterpriseUser?.firstName}${(assignee[1] || []).length}${enterpriseUser?.avatarColor}`}
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={(
                                <Avatar className="working-companies">
                                  {(assignee[1] || []).length}
                                </Avatar>
                              )}
                            >
                              <Avatar
                                className={`initials ${darkInitials.includes(enterpriseUser?.avatarColor) ? 'dark' : 'light'}`}
                                style={{
                                  backgroundColor: enterpriseUser?.avatarColor,
                                  color: darkInitials.includes(enterpriseUser?.avatarColor) ? '#49454F' : '#FAFAFD',
                                }}
                              >
                                {getInitials(`${enterpriseUser?.firstName} ${enterpriseUser?.lastName}`)}
                              </Avatar>
                            </Badge>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <Tooltip
                  id="name-tooltip"
                  title={`Go to ${investorFirmName || 'Investor'} details`}
                  disableFocusListener
                  arrow
                  placement="top"
                >
                  <Button
                    className="expanded-links investor-details"
                    onClick={() => setInvestorCompanyDetailToView(projectData)}
                    onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                    onMouseLeave={() => setInsideElementHover(false)}
                  >
                    <span>
                      {`Go to ${truncateStr(investorFirmName || 'Investor', 18)} details`}
                    </span>
                  </Button>
                </Tooltip>
                <Select
                  className="expanded-links dropdown-select"
                  open={dropdownIsOpen.priorityDropdown}
                  value={priority || ''}
                  renderValue={() => <span className="priority-label">Priority</span>}
                  onOpen={() => setDropdownIsOpen({ ...dropdownIsOpen, priorityDropdown: true })}
                  onChange={async (e) => {
                    updateProjectList(e.target.value);
                    updatePriority(e.target.value);
                  }}
                  IconComponent={ExpandMoreIcon}
                  MenuProps={{ disableScrollLock: true, disablePortal: true }}
                  onMouseEnter={() => { if (!insideElementHover) setInsideElementHover(true); }}
                  onMouseLeave={() => setInsideElementHover(false)}
                  onClose={() => setDropdownIsOpen({ ...dropdownIsOpen, priorityDropdown: false })}
                >
                  <MenuItem value="1">
                    <NotificationImportantOutlinedIcon className="high-icon" />
                    High
                  </MenuItem>
                  <MenuItem value="2">
                    <ArrowDownwardIcon sx={{ visibility: 'hidden', marginRight: '18px' }} />
                    Normal
                  </MenuItem>
                  <MenuItem value="3">
                    <ArrowDownwardIcon className="low-icon" />
                    Low
                  </MenuItem>
                </Select>
                <div className="due-dates">
                  <span>
                    <CalendarMonthOutlinedIcon />
                    {projectData?.clientInfoDueDate}
                    &nbsp;info due&nbsp;
                  </span>
                  <hr />
                  <span>
                    <CalendarMonthOutlinedIcon />
                    {projectData?.auditDate}
                    &nbsp;audit date&nbsp;
                  </span>
                  <hr />
                  <span>
                    <CalendarMonthOutlinedIcon />
                    {projectData?.valuationDate}
                    &nbsp;valuation date&nbsp;
                  </span>
                </div>
              </div>
              <div className="edited-by">
                {lastModifiedUser && (
                  <p>
                    Edited&nbsp;
                    {projectData.lastModified.split(' ')[1]}
                    &nbsp;by&nbsp;
                    {`${findEnterpriseUser(projectData.lastRequestUserId)?.firstName} ${findEnterpriseUser(projectData.lastRequestUserId)?.lastName}`}
                  </p>
                )}
              </div>
              <Button
                className="delete-button"
                startIcon={<DeleteOutlineOutlinedIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
                variant="contained"
                fullWidth
              >
              Delete Project
              </Button>
            </div>
          </div>
        </AnimateHeight>
      </div>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        className="delete-project-dialog"
      >
        <IconButton
          className="close-icon"
          onClick={(e) => {
            e.stopPropagation();
            if (!isDeleting) {
              setDeleteDialogOpen(false);
            }
          }}
          disabled={isDeleting}
        >
          <CloseIcon />
        </IconButton>
        <div className="box-header">
          <WarningRoundedIcon className="warning-icon" />
          <h4>Delete Project</h4>
        </div>
        <DialogContent>
          <Typography className="dialog-text">
            <span className="warning-text">Warning:</span> You are about to permanently delete this project. 
            <br />
            <span className="caution-text">This action cannot be reversed and all associated data will be permanently removed.</span>
          </Typography>
        </DialogContent>
        <DialogActions className="dialog-box-buttons">
          <Button 
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(false);
            }}
            className="cancel-btn"
            disabled={isDeleting || deleteSuccess}
          >
            Cancel
          </Button>
          <Button 
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteProject();
            }} 
            className={`delete-btn ${isDeleting ? 'deleting' : ''} ${deleteSuccess ? 'success' : ''}`}
            variant="contained"
            disabled={isDeleting || deleteSuccess}
          >
            {isDeleting ? (
              <div className="delete-loading">
                <div className="spinner"></div>
                <span className="delete-text">Deleting...</span>
              </div>
            ) : deleteSuccess ? (
              <div className="delete-success">
                <CheckCircleOutlineIcon className="success-icon" />
                <span className="success-text">Deleted</span>
              </div>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!deleteError}
        autoHideDuration={3000}
        onClose={() => setDeleteError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setDeleteError(null)} 
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {deleteError}
        </Alert>
      </Snackbar>
    </div>
  );
}

ProjectStatusCard.propTypes = {
  projectData: PropTypes.object.isRequired,
  // setProjectToView: PropTypes.func.isRequired,
  // enterpriseUsers: PropTypes.array.isRequired,
  projectList: PropTypes.array.isRequired,
  setProjectList: PropTypes.func.isRequired,
  // setInvestorCompanyDetailToView: PropTypes.func.isRequired,
  dataEntry: PropTypes.bool,
  preparer: PropTypes.bool,
  deliver: PropTypes.bool,
};

ProjectStatusCard.defaultProps = {
  dataEntry: false,
  preparer: false,
  deliver: false,
};
